import { useEffect, useState } from "react";
import { Plus, Trash2, Shield, User, Mail, Phone, Edit2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

const ROLE_BADGE = {
  admin: "bg-forest text-bone",
  agent: "bg-stone text-forest",
};

export default function AgentsManagement() {
  const { agent: currentAgent } = useAuth();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "agent", phone: "" });
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/agents");
      setAgents(data);
    } catch {
      toast.error("Could not load agents");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const reset = () => setForm({ name: "", email: "", password: "", role: "agent", phone: "" });

  const openNew = () => {
    setEditing(null);
    reset();
    setOpen(true);
  };

  const openEdit = (a) => {
    setEditing(a);
    setForm({ name: a.name, email: a.email, password: "", role: a.role || "agent", phone: a.phone || "" });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editing) {
        const payload = { name: form.name, role: form.role, phone: form.phone };
        if (form.password) payload.password = form.password;
        await api.patch(`/agents/${editing.id}`, payload);
        toast.success("Agent updated");
      } else {
        if (!form.password) {
          toast.error("Password is required for new agents");
          setSubmitting(false);
          return;
        }
        await api.post("/agents", form);
        toast.success("Agent created");
      }
      setOpen(false);
      reset();
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Operation failed");
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/agents/${id}`);
      toast.success("Agent removed");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not delete");
    }
  };

  return (
    <div className="p-6 lg:p-10">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-clay font-semibold mb-2">Team</div>
          <h1 className="font-heading text-3xl md:text-4xl tracking-tight font-medium">Agents &amp; admins</h1>
          <p className="text-sage mt-2 text-sm">Manage who can access the InsureWell agent portal.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openNew} className="bg-forest hover:bg-forestdark text-bone rounded-sm h-10" data-testid="add-agent-btn">
              <Plus className="w-4 h-4 mr-2" /> New agent
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white border-line">
            <DialogHeader>
              <DialogTitle className="font-heading">{editing ? "Edit agent" : "New agent"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4 mt-2" data-testid="agent-form">
              <div>
                <Label className="text-forest">Full name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="mt-1.5 h-11 border-line input-clean" data-testid="agent-form-name" />
              </div>
              <div>
                <Label className="text-forest">Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required disabled={!!editing} className="mt-1.5 h-11 border-line input-clean" data-testid="agent-form-email" />
              </div>
              <div>
                <Label className="text-forest">Phone <span className="text-sage font-normal">(for SMS notifications)</span></Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+15551234567" className="mt-1.5 h-11 border-line input-clean" data-testid="agent-form-phone" />
              </div>
              <div>
                <Label className="text-forest">Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger className="mt-1.5 h-11 border-line" data-testid="agent-form-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="agent">Agent</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-forest">{editing ? "New password " : "Password "}<span className="text-sage font-normal">{editing ? "(leave blank to keep current)" : ""}</span></Label>
                <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required={!editing} className="mt-1.5 h-11 border-line input-clean" data-testid="agent-form-password" />
              </div>
              <DialogFooter>
                <Button type="submit" disabled={submitting} className="bg-forest hover:bg-forestdark text-bone rounded-sm" data-testid="agent-form-submit">
                  {submitting ? "Saving…" : editing ? "Save changes" : "Create agent"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white border border-line rounded-sm overflow-hidden">
        <Table data-testid="agents-table">
          <TableHeader>
            <TableRow className="bg-cream hover:bg-cream">
              <TableHead className="text-forest font-medium">Name</TableHead>
              <TableHead className="text-forest font-medium">Email</TableHead>
              <TableHead className="text-forest font-medium">Phone</TableHead>
              <TableHead className="text-forest font-medium">Role</TableHead>
              <TableHead className="text-right text-forest font-medium">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={5} className="text-center text-sage py-10">Loading…</TableCell></TableRow>}
            {!loading && agents.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-sage py-10">No agents yet</TableCell></TableRow>}
            {!loading && agents.map((a) => (
              <TableRow key={a.id} className="hover:bg-cream/50" data-testid={`agent-row-${a.id}`}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-clay" />
                    <span className="font-medium text-forest">{a.name}</span>
                  </div>
                </TableCell>
                <TableCell className="text-sm text-sage">
                  <span className="inline-flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" />{a.email}</span>
                </TableCell>
                <TableCell className="text-sm text-sage">
                  {a.phone ? <span className="inline-flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" />{a.phone}</span> : "—"}
                </TableCell>
                <TableCell>
                  <span className={`text-xs px-2 py-1 rounded-sm ${ROLE_BADGE[a.role || "agent"]} inline-flex items-center gap-1`}>
                    {a.role === "admin" && <Shield className="w-3 h-3" />} {a.role || "agent"}
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  <div className="inline-flex items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(a)} className="text-clay hover:text-claydark hover:bg-cream" data-testid={`edit-agent-${a.id}`}>
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    {a.id !== currentAgent?.id && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="sm" className="text-sage hover:text-danger hover:bg-cream" data-testid={`delete-agent-${a.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="bg-white border-line">
                          <AlertDialogHeader>
                            <AlertDialogTitle>Remove {a.name}?</AlertDialogTitle>
                            <AlertDialogDescription>
                              They will lose access to the agent portal immediately. This cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => remove(a.id)} className="bg-danger hover:bg-danger/90 text-bone rounded-sm" data-testid={`confirm-delete-${a.id}`}>
                              Remove
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
